<script setup>
// NOTICE: KAPTION-INTEGRATION
// sales-pipeline.SETTINGS.1 — administrator-only pipeline settings: which labels
// drive the board and their order, column name, colour (default = label colour),
// Won/Lost/none, WIP limit, resolve-on-enter; the pipeline scope (inboxes, teams,
// statuses) and the optional "unstaged" column. The server re-validates all of it
// and refuses non-administrators (403).

import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { DEFAULT_STATUSES } from 'dashboard/helper/kaptionSalesPipeline.js';

const props = defineProps({
  pipeline: { type: Object, default: null },
  saving: { type: Boolean, default: false },
  error: { type: String, default: '' },
});

const emit = defineEmits(['save', 'createDefault', 'close']);

const MAX_STAGES = 10;
const { t } = useI18n();
const store = useStore();

const labels = computed(() => store.getters['labels/getLabels'] || []);
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);
const labelById = id => labels.value.find(l => l.id === Number(id));

const form = reactive({
  id: 'default',
  name: '',
  scope: { inbox_ids: [], team_ids: [], statuses: [...DEFAULT_STATUSES] },
  unstaged_column: { enabled: false, name: '' },
  stages: [],
});
const localError = ref('');

function load(pipeline) {
  form.id = pipeline?.id || 'default';
  form.name =
    pipeline?.name ||
    t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.DEFAULT_NAME');
  form.scope = {
    inbox_ids: [...(pipeline?.scope?.inbox_ids || [])],
    team_ids: [...(pipeline?.scope?.team_ids || [])],
    statuses: [...(pipeline?.scope?.statuses || DEFAULT_STATUSES)],
  };
  form.unstaged_column = {
    enabled: !!pipeline?.unstaged_column?.enabled,
    name:
      pipeline?.unstaged_column?.name ||
      t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.UNSTAGED_DEFAULT'),
  };
  form.stages = (pipeline?.stages || []).map(stage => ({
    label_id: stage.label_id,
    name: stage.name,
    // null = follow the label colour
    color:
      stage.color && stage.color !== stage.label_color ? stage.color : null,
    terminal: stage.terminal || 'none',
    wip_limit: stage.wip_limit || '',
    resolve_on_enter: !!stage.resolve_on_enter,
  }));
}

watch(() => props.pipeline, load, { immediate: true });

const usedLabelIds = computed(() => form.stages.map(s => Number(s.label_id)));
const availableLabels = stage =>
  labels.value.filter(
    l => l.id === Number(stage.label_id) || !usedLabelIds.value.includes(l.id)
  );

const effectiveColor = stage =>
  stage.color || labelById(stage.label_id)?.color || '#94A3B8';

function addStage() {
  const next = labels.value.find(l => !usedLabelIds.value.includes(l.id));
  if (!next || form.stages.length >= MAX_STAGES) return;
  form.stages.push({
    label_id: next.id,
    name: next.title,
    color: null,
    terminal: 'none',
    wip_limit: '',
    resolve_on_enter: false,
  });
}

function moveStage(index, delta) {
  const target = index + delta;
  if (target < 0 || target >= form.stages.length) return;
  const [stage] = form.stages.splice(index, 1);
  form.stages.splice(target, 0, stage);
}

function onLabelChange(stage) {
  const label = labelById(stage.label_id);
  if (label && (!stage.name || labels.value.some(l => l.title === stage.name)))
    stage.name = label.title;
}

function onTerminalChange(stage) {
  if (stage.terminal !== 'none') stage.resolve_on_enter = true;
}

function toggleId(list, id) {
  const index = list.indexOf(id);
  if (index >= 0) list.splice(index, 1);
  else list.push(id);
}

function submit() {
  localError.value = '';
  if (!form.stages.length) {
    localError.value = t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.NEED_STAGE');
    return;
  }
  emit('save', {
    pipelines: [
      {
        id: form.id,
        name: form.name,
        scope: form.scope,
        unstaged_column: form.unstaged_column,
        stages: form.stages.map(stage => ({
          label_id: Number(stage.label_id),
          name: stage.name,
          color: stage.color,
          terminal: stage.terminal === 'none' ? null : stage.terminal,
          wip_limit: stage.wip_limit === '' ? null : Number(stage.wip_limit),
          resolve_on_enter: stage.resolve_on_enter,
        })),
      },
    ],
  });
}
</script>

<template>
  <div
    class="flex-1 min-h-0 overflow-y-auto bg-n-background"
    data-testid="pipeline-settings"
  >
    <div class="max-w-5xl mx-auto px-4 py-5 flex flex-col gap-6">
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="size-8 !p-0 inline-flex items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
            :title="t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.BACK')"
            data-testid="pipeline-settings-back"
            @click="emit('close')"
          >
            <i class="i-lucide-arrow-left size-4" />
          </button>
          <div>
            <h2 class="m-0 text-base font-semibold text-n-slate-12">
              {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.TITLE') }}
            </h2>
            <p class="m-0 text-xs text-n-slate-11">
              {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.SUBTITLE') }}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Button
            v-if="!pipeline"
            :label="
              t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.CREATE_DEFAULT')
            "
            icon="i-lucide-wand-sparkles"
            variant="outline"
            color="slate"
            size="sm"
            data-testid="pipeline-create-default"
            @click="emit('createDefault')"
          />
          <Button
            :label="t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.SAVE')"
            color="teal"
            size="sm"
            :is-loading="saving"
            data-testid="pipeline-settings-save"
            @click="submit"
          />
        </div>
      </div>

      <p
        v-if="error || localError"
        class="m-0 px-3 py-2 rounded-lg bg-n-ruby-3 text-n-ruby-11 text-sm"
        data-testid="pipeline-settings-error"
      >
        {{ error || localError }}
      </p>
      <p
        v-if="pipeline?.missing_label_ids?.length"
        class="m-0 px-3 py-2 rounded-lg bg-n-amber-3 text-n-amber-11 text-sm"
      >
        {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.MISSING_LABELS') }}
      </p>

      <label class="flex flex-col gap-1 max-w-sm">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.NAME') }}
        </span>
        <input
          v-model="form.name"
          type="text"
          maxlength="60"
          class="reset-base h-8 px-2 text-sm rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
          data-testid="pipeline-settings-name"
        />
      </label>

      <!-- Stages -->
      <section class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <h3 class="m-0 text-sm font-semibold text-n-slate-12">
            {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.STAGES') }}
          </h3>
          <span class="text-xs text-n-slate-10">
            {{ form.stages.length }}/{{ MAX_STAGES }}
          </span>
        </div>
        <p class="m-0 text-xs text-n-slate-11">
          {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.STAGES_HELP') }}
        </p>
        <div class="rounded-lg border border-n-weak overflow-x-auto">
          <table class="w-full text-xs">
            <thead class="bg-n-alpha-1 text-n-slate-11">
              <tr>
                <th class="px-2 py-2 text-left font-medium w-14" />
                <th class="px-2 py-2 text-left font-medium">
                  {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.LABEL') }}
                </th>
                <th class="px-2 py-2 text-left font-medium">
                  {{
                    t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.COLUMN_NAME')
                  }}
                </th>
                <th class="px-2 py-2 text-left font-medium">
                  {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.COLOR') }}
                </th>
                <th class="px-2 py-2 text-left font-medium">
                  {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.TERMINAL') }}
                </th>
                <th class="px-2 py-2 text-left font-medium">
                  {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.WIP') }}
                </th>
                <th class="px-2 py-2 text-left font-medium">
                  {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.RESOLVE') }}
                </th>
                <th class="w-8" />
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(stage, index) in form.stages"
                :key="stage.label_id"
                class="border-t border-n-weak"
                data-testid="pipeline-settings-stage"
              >
                <td class="px-2 py-1.5">
                  <div class="flex items-center gap-0.5">
                    <button
                      type="button"
                      class="p-0.5 rounded text-n-slate-10 hover:bg-n-alpha-2 disabled:opacity-30"
                      :disabled="index === 0"
                      :title="
                        t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.MOVE_UP')
                      "
                      @click="moveStage(index, -1)"
                    >
                      <i class="i-lucide-arrow-up size-3.5 block" />
                    </button>
                    <button
                      type="button"
                      class="p-0.5 rounded text-n-slate-10 hover:bg-n-alpha-2 disabled:opacity-30"
                      :disabled="index === form.stages.length - 1"
                      :title="
                        t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.MOVE_DOWN')
                      "
                      @click="moveStage(index, 1)"
                    >
                      <i class="i-lucide-arrow-down size-3.5 block" />
                    </button>
                  </div>
                </td>
                <td class="px-2 py-1.5">
                  <select
                    v-model.number="stage.label_id"
                    class="!h-7 !mb-0 !py-0 !text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 min-w-[9rem]"
                    data-testid="pipeline-settings-label"
                    @change="onLabelChange(stage)"
                  >
                    <option
                      v-for="label in availableLabels(stage)"
                      :key="label.id"
                      :value="label.id"
                    >
                      {{ label.title }}
                    </option>
                  </select>
                </td>
                <td class="px-2 py-1.5">
                  <input
                    v-model="stage.name"
                    type="text"
                    maxlength="60"
                    class="reset-base h-7 px-2 text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 w-36"
                  />
                </td>
                <td class="px-2 py-1.5">
                  <div class="flex items-center gap-1">
                    <input
                      type="color"
                      :value="effectiveColor(stage)"
                      class="size-7 !mb-0 p-0.5 rounded border border-n-weak bg-n-solid-1 cursor-pointer"
                      @input="stage.color = $event.target.value"
                    />
                    <button
                      v-if="stage.color"
                      type="button"
                      class="text-[10px] text-n-slate-10 hover:text-n-slate-12 underline"
                      @click="stage.color = null"
                    >
                      {{
                        t(
                          'KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.USE_LABEL_COLOR'
                        )
                      }}
                    </button>
                  </div>
                </td>
                <td class="px-2 py-1.5">
                  <select
                    v-model="stage.terminal"
                    class="!h-7 !mb-0 !py-0 !text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12"
                    data-testid="pipeline-settings-terminal"
                    @change="onTerminalChange(stage)"
                  >
                    <option value="none">
                      {{
                        t(
                          'KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.TERMINAL_NONE'
                        )
                      }}
                    </option>
                    <option value="won">
                      {{
                        t(
                          'KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.TERMINAL_WON'
                        )
                      }}
                    </option>
                    <option value="lost">
                      {{
                        t(
                          'KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.TERMINAL_LOST'
                        )
                      }}
                    </option>
                  </select>
                </td>
                <td class="px-2 py-1.5">
                  <input
                    v-model="stage.wip_limit"
                    type="number"
                    min="1"
                    max="9999"
                    :placeholder="
                      t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.WIP_NONE')
                    "
                    class="reset-base h-7 px-2 text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 w-20"
                    data-testid="pipeline-settings-wip"
                  />
                </td>
                <td class="px-2 py-1.5">
                  <Switch v-model="stage.resolve_on_enter" />
                </td>
                <td class="px-2 py-1.5">
                  <button
                    type="button"
                    class="p-1 rounded text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-alpha-2"
                    :title="
                      t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.REMOVE_STAGE')
                    "
                    @click="form.stages.splice(index, 1)"
                  >
                    <i class="i-lucide-trash-2 size-3.5 block" />
                  </button>
                </td>
              </tr>
              <tr v-if="!form.stages.length">
                <td colspan="8" class="px-3 py-4 text-center text-n-slate-10">
                  {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.NO_STAGES') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div>
          <Button
            :label="t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.ADD_STAGE')"
            icon="i-lucide-plus"
            variant="ghost"
            color="slate"
            size="sm"
            :disabled="
              form.stages.length >= MAX_STAGES ||
              usedLabelIds.length >= labels.length
            "
            data-testid="pipeline-settings-add-stage"
            @click="addStage"
          />
        </div>
      </section>

      <!-- Unstaged column -->
      <section class="flex flex-col gap-2">
        <h3 class="m-0 text-sm font-semibold text-n-slate-12">
          {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.UNSTAGED') }}
        </h3>
        <div class="flex flex-wrap items-center gap-3">
          <Switch v-model="form.unstaged_column.enabled" />
          <span class="text-xs text-n-slate-11">
            {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.UNSTAGED_HELP') }}
          </span>
          <input
            v-if="form.unstaged_column.enabled"
            v-model="form.unstaged_column.name"
            type="text"
            maxlength="60"
            class="reset-base h-7 px-2 text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 w-40"
          />
        </div>
      </section>

      <!-- Scope -->
      <section class="flex flex-col gap-3">
        <div>
          <h3 class="m-0 text-sm font-semibold text-n-slate-12">
            {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.SCOPE') }}
          </h3>
          <p class="m-0 text-xs text-n-slate-11">
            {{ t('KAPTION_PLATFORM.SALES_PIPELINE.SETTINGS.SCOPE_HELP') }}
          </p>
        </div>
        <div class="grid gap-4 md:grid-cols-2">
          <fieldset class="flex flex-col gap-1">
            <legend class="text-xs font-medium text-n-slate-11 mb-1">
              {{ t('KAPTION_PLATFORM.SALES_PIPELINE.FILTERS.INBOX') }}
            </legend>
            <label
              v-for="inbox in inboxes"
              :key="inbox.id"
              class="flex items-center gap-2 text-xs text-n-slate-12"
            >
              <input
                type="checkbox"
                class="!mb-0"
                :checked="form.scope.inbox_ids.includes(inbox.id)"
                @change="toggleId(form.scope.inbox_ids, inbox.id)"
              />
              {{ inbox.name }}
            </label>
          </fieldset>
          <fieldset class="flex flex-col gap-1">
            <legend class="text-xs font-medium text-n-slate-11 mb-1">
              {{ t('KAPTION_PLATFORM.SALES_PIPELINE.FILTERS.TEAM') }}
            </legend>
            <label
              v-for="team in teams"
              :key="team.id"
              class="flex items-center gap-2 text-xs text-n-slate-12"
            >
              <input
                type="checkbox"
                class="!mb-0"
                :checked="form.scope.team_ids.includes(team.id)"
                @change="toggleId(form.scope.team_ids, team.id)"
              />
              {{ team.name }}
            </label>
          </fieldset>
        </div>
      </section>
    </div>
  </div>
</template>
